/**
 * Dice rolling and game mechanics types
 */

export interface RollResult {
  raw: number;
  target: number;
  modifiers: Modifier[];
  total: number;
  degrees: number;
  success: boolean;
  tags: string[];
  timestamp: Date;
  proficiencyPenalty?: {
    applied: boolean;
    amount: number;
    reason: string;
    suggestedTalents: string[];
  };
  entryId?: string; // ID of the log entry if logged
}

export interface Modifier {
  name: string;
  value: number;
  source: ModifierSource;
  description?: string;
}

export enum ModifierSource {
  Characteristic = 'characteristic',
  Skill = 'skill',
  Talent = 'talent',
  Trait = 'trait',
  Equipment = 'equipment',
  Situational = 'situational',
  Manual = 'manual',
  Proficiency = 'proficiency'
}

export interface DiceRollOptions {
  characteristic?: number;
  skill?: number;
  modifiers?: Modifier[];
  difficulty?: Difficulty;
  rollType?: RollType;
}

export enum Difficulty {
  Trivial = 60,
  Elementary = 50,
  Simple = 40,
  Easy = 30,
  Routine = 20,
  Ordinary = 10,
  Challenging = 0,
  Difficult = -10,
  Hard = -20,
  VeryHard = -30,
  Arduous = -40,
  Punishing = -50,
  Hellish = -60
}

export enum RollType {
  Characteristic = 'characteristic',
  Skill = 'skill',
  Damage = 'damage',
  Initiative = 'initiative',
  Other = 'other'
}

export interface DamageRoll {
  weapon: string;
  baseDamage: string;
  strengthBonus: number;
  degreesOfSuccess: number;
  modifiers: Modifier[];
  total: number;
  penetration: number;
  type: DamageType;
  // Reroll support
  dieRoll?: number; // The raw die result (e.g. 2)
  rerollThreshold?: number; // If set, rolling <= this allows a reroll
  canReroll?: boolean; // Convenience flag
  ruleNotes?: string[]; // Active special rules affecting this damage (Tearing, Proven, etc.)
}

export enum DamageType {
  Impact = 'impact',
  Rending = 'rending',
  Energy = 'energy',
  Explosive = 'explosive'
}

export interface MultipleHitsResult {
  totalHits: number;
  semiAutoHits: number;
  fullAutoHits: number; // For full-auto burst
  scatterHits: number;
  hitLocations: HitLocation[];
  cappedByRoF: boolean;
  maxRoF: number;
}

export interface SemiAutoBurstOptions {
  allowMovement: boolean; // Whether Pistol/Basic weapon can move
  isMoving: boolean; // Whether player chooses to move
  hasScatter: boolean; // Whether weapon has Scatter quality
  isPointBlank: boolean; // Whether at point blank range
  semiAutoRoF: number; // Weapon's semi-auto rate of fire
}

export interface FullAutoBurstOptions {
  allowMovement: boolean; // Whether Pistol/Basic weapon can move
  isMoving: boolean; // Whether player chooses to move
  hasScatter: boolean; // Whether weapon has Scatter quality
  isPointBlank: boolean; // Whether at point blank range
  fullAutoRoF: number; // Weapon's full-auto rate of fire
}

export interface CombatRoll extends RollResult {
  attackType: AttackType;
  weapon?: string;
  actionId?: string;
  damage?: DamageRoll;
  hitLocation?: HitLocation; // Single hit location (backward compatible)
  multipleHits?: MultipleHitsResult; // Multiple hits data
}

export enum AttackType {
  Melee = 'melee',
  Ranged = 'ranged',
  Psychic = 'psychic'
}

export enum HitLocation {
  Head = 'Head',
  RightArm = 'Right Arm',
  LeftArm = 'Left Arm',
  Body = 'Body',
  RightLeg = 'Right Leg',
  LeftLeg = 'Left Leg'
}

export interface RollHistory {
  rolls: RollResult[];
  sessionStart: Date;
  maxEntries: number;
}

// Utility types for dice mechanics
export type D100Roll = number; // 1-100
export type DegreesOfSuccess = number; // Positive for success, negative for failure
export type CharacteristicValue = number; // 1-100
export type SkillBonus = number; // Training level bonus
